import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { GlassPanel } from "../components/GlassPanel";
import { Plus, Pencil, Trash2, X, Check, XCircle, Globe, MapPin, ArrowUpDown, MailOpen, PhoneCall, EyeOff, ArrowRightCircle } from "lucide-react";
import { toast } from "sonner";
import EmbedCodePanel from "../components/EmbedCodePanel";
import ContactPickerField from "../components/ContactPickerField";
import DetailsModal, { CustomerVehiclePhoto } from "../components/DetailsModal";
import StatusPipeline from "../components/StatusPipeline";
import { LOCATION_TYPES, jobTypeMeta, blankJobItem, normalizeItem, serviceTypesLabel } from "../lib/jobTypes";
import { SUB_STATUSES } from "../lib/pipeline";
import { relativeTime, absoluteTime } from "../lib/utils";
import { useUnviewedLeads } from "../hooks/useUnviewedLeads";
import JobTypeMultiSelect from "../components/JobTypeMultiSelect";
import JobItemForm from "../components/JobItemForm";

const SUB_STATUS_PILL = {
  new: "pill-info",
  contacted: "pill-warning",
  qualified: "pill-success",
  lost: "pill-error",
};

// Sort options for the Leads list. Default is arrived_desc (newest at the
// top) mirroring the Quotes tab. Keys/values must NOT overlap between
// pages so the two persisted localStorage keys stay independent.
const SORT_OPTIONS = [
  { value: "arrived_desc", label: "Newest lead (default)" },
  { value: "arrived_asc",  label: "Oldest lead first" },
  { value: "customer_asc", label: "Customer name — A to Z" },
  { value: "customer_desc",label: "Customer name — Z to A" },
  { value: "updated_desc", label: "Recently updated" },
];
const _leadArrivedKey = (l) => l.lead_arrived_at || l.created_at || "";
const _leadCustomerKey = (l) => (l.contact_name || l.name || "").toLowerCase();
const _leadUpdatedKey = (l) => l.updated_at || l.created_at || "";
const SORT_COMPARATORS = {
  arrived_desc: (a, b) => _leadArrivedKey(b).localeCompare(_leadArrivedKey(a)),
  arrived_asc:  (a, b) => _leadArrivedKey(a).localeCompare(_leadArrivedKey(b)),
  customer_asc: (a, b) => _leadCustomerKey(a).localeCompare(_leadCustomerKey(b)),
  customer_desc:(a, b) => _leadCustomerKey(b).localeCompare(_leadCustomerKey(a)),
  updated_desc: (a, b) => _leadUpdatedKey(b).localeCompare(_leadUpdatedKey(a)),
};

const blank = {
  contact_id: "",
  contact_name: "", contact_email: "", contact_phone: "", contact_address: "",
  vehicle: "", service: "", notes: "",
  main_status: "lead", sub_status: "new",
  job_type: "car", location_type: "on_site", location_address: "",
  job_items: [],
};

export default function LeadsPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [showEmbed, setShowEmbed] = useState(false);
  const [detailsFor, setDetailsFor] = useState(null);
  const [sortBy, setSortBy] = useState(() => {
    try { return localStorage.getItem("wc_leads_sort") || "arrived_desc"; }
    catch (_) { return "arrived_desc"; }
  });
  const { refresh: refreshUnviewedBadge } = useUnviewedLeads();

  const load = async () => {
    const { data } = await api.get("/jobs", { params: { main_status: "lead" } });
    setItems(data);
  };

  useEffect(() => { load(); }, []);

  // Persist the admin's preferred sort so it survives reloads.
  useEffect(() => {
    try { localStorage.setItem("wc_leads_sort", sortBy); } catch (_) { /* noop */ }
  }, [sortBy]);

  const sortedItems = useMemo(() => {
    const cmp = SORT_COMPARATORS[sortBy] || SORT_COMPARATORS.arrived_desc;
    return [...items].sort(cmp);
  }, [items, sortBy]);

  const openLeadDetails = async (lead) => {
    setDetailsFor(lead);
    // Fire-and-forget: first open marks the lead as seen so the red badge clears.
    if (!lead.viewed_at) {
      try {
        await api.post(`/jobs/${lead.id}/view`);
        setItems((prev) => prev.map((x) => (x.id === lead.id ? { ...x, viewed_at: new Date().toISOString() } : x)));
        refreshUnviewedBadge();
      } catch (_) {
        /* non-blocking */
      }
    }
  };

  const openCreate = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (item) => {
    setEditing(item);
    // Backwards-compat: if the lead pre-dates the multi-item schema, seed a
    // single job_item from the legacy job_type + vehicle fields so the user
    // can still edit it inline.
    let hydratedItems = item.job_items || [];
    if (!hydratedItems.length && item.job_type) {
      hydratedItems = [{
        ...blankJobItem(item.job_type),
        notes: item.vehicle || item.service || "",
      }];
    }
    // Normalise every item into the new {installations:[...]} shape so
    // legacy leads with top-level installation_type still render correctly.
    hydratedItems = hydratedItems.map(normalizeItem);
    setForm({
      ...blank,
      ...item,
      job_items: hydratedItems,
      // Normalise legacy lead fields onto contact_* if present
      contact_name: item.contact_name || item.name || "",
      contact_email: item.contact_email || item.email || "",
      contact_phone: item.contact_phone || item.phone || "",
      contact_address: item.contact_address || item.address || "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.contact_id && !form.contact_name) {
      toast.error("Please select or add a customer first");
      return;
    }
    // Derive legacy fields from job_items so downstream views (search,
    // Quotes conversion, etc.) still show something meaningful without
    // needing to know about the new schema yet.
    const items = form.job_items || [];
    const primary = items[0];
    const legacyVehicle = items
      .map((it) => it.year || it.make || it.model
        ? [it.year, it.make, it.model].filter(Boolean).join(" ")
        : (it.notes || "").split("\n")[0])
      .filter(Boolean)
      .join(" · ");
    const legacyService = Array.from(new Set(items.map((i) => jobTypeMeta(i.job_type).label))).join(" + ");
    // Auto-sum installation prices across all items (new nested schema).
    const itemsSum = items.reduce((s, it) => {
      const insts = it.installations || [];
      return s + insts.reduce((ss, inst) => ss + (Number(inst.price) || 0), 0)
              + (insts.length === 0 ? (Number(it.price) || 0) : 0);
    }, 0);
    const payload = {
      ...form,
      job_items: items,
      job_type: primary?.job_type || form.job_type || "car",
      vehicle: legacyVehicle || form.vehicle || "",
      service: legacyService || form.service || "",
      amount: form.amount != null && form.amount !== "" && Number(form.amount) > 0 ? Number(form.amount) : itemsSum,
      main_status: "lead",
      sub_status: form.sub_status || "new",
    };
    try {
      if (editing) {
        await api.put(`/jobs/${editing.id}`, payload);
        toast.success("Lead updated");
      } else {
        await api.post("/jobs", payload);
        toast.success("Lead created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/jobs/${id}`);
    toast.success("Lead deleted");
    load();
  };

  const convertToQuote = async (lead) => {
    if (!window.confirm(`Approve ${lead.contact_name || lead.name} and move them to Quotes?`)) return;
    try {
      await api.post(`/jobs/${lead.id}/advance`, { main_status: "quote", sub_status: "unquoted" });
      toast.success("Approved — moved to Quotes (unquoted, set an amount when ready)");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const rejectLead = async (lead) => {
    // Reject = mark the lead as lost so it drops out of the active pipeline
    // but stays on the record for auditing / conversion-rate math. We keep
    // it on the Leads tab (main_status="lead") rather than moving it, so
    // office staff can still see why it was rejected in the notes.
    if (!window.confirm(`Reject ${lead.contact_name || lead.name}? They'll be marked as lost.`)) return;
    try {
      await api.post(`/jobs/${lead.id}/advance`, { main_status: "lead", sub_status: "lost" });
      toast.success("Lead rejected — marked as lost");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-label">Pipeline</div>
          <h1 className="font-heading text-4xl font-light tracking-tighter text-white">Leads</h1>
        </div>
        <div className="flex gap-2 flex-wrap items-end">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5 mb-1">
              <ArrowUpDown className="w-3 h-3" /> Sort
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              data-testid="leads-sort"
              className="bg-black/40 border border-white/10 rounded-full px-3 py-2 text-xs text-white focus:outline-none focus:border-white/30 pr-8"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} className="bg-neutral-900">{o.label}</option>
              ))}
            </select>
          </div>
          <button
            data-testid="leads-embed-toggle"
            onClick={() => setShowEmbed((v) => !v)}
            className="flex items-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 text-white px-4 py-2.5 rounded-full text-sm"
          >
            <Globe className="w-4 h-4" /> {showEmbed ? "Hide" : "Get embed code"}
          </button>
          <button
            data-testid="leads-create-button"
            onClick={openCreate}
            className="flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-slate-100 transition"
          >
            <Plus className="w-4 h-4" /> New lead
          </button>
        </div>
      </div>

      {showEmbed && <EmbedCodePanel />}

      <AbandonedDraftsPanel onPromoted={load} />

      <GlassPanel className="!p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead className="text-left">
              <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-white/5">
                <th className="px-5 py-4">Name</th>
                <th className="px-5 py-4">Vehicle</th>
                <th className="px-5 py-4">Service</th>
                <th className="px-5 py-4">Service type</th>
                <th className="px-5 py-4">Contact</th>
                <th className="px-5 py-4">Received</th>
                <th className="px-5 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-500 italic">No leads yet. Add your first one.</td></tr>
              )}
              {sortedItems.map((it) => {
                const displayName = it.contact_name || it.name || "—";
                const isUnviewed = !it.viewed_at;
                return (
                <React.Fragment key={it.id}>
                {/* Stage row — full-width visual pipeline at the top of the record */}
                <tr
                  data-testid={`lead-stage-row-${it.id}`}
                  className={`bg-white/[0.02] border-t border-white/5 ${isUnviewed ? "bg-red-500/[0.04]" : ""}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <td colSpan={7} className="px-5 pt-3 pb-2">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-[10px] uppercase tracking-widest text-slate-500 shrink-0">Status</span>
                      <span data-testid={`lead-stage-pill-${it.id}`} className={`pill ${SUB_STATUS_PILL[it.sub_status] || "pill-muted"} self-start`}>{it.sub_status || "new"}</span>
                      <div className="flex-1 min-w-[240px]">
                        <StatusPipeline job={it} onChange={load} compact inline hideMain />
                      </div>
                      {it.source && it.source !== "manual" && (
                        <span className="text-[9px] uppercase tracking-widest text-slate-500 flex items-center gap-1 shrink-0">
                          <Globe className="w-2.5 h-2.5" /> {it.source}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
                {/* Details row */}
                <tr
                  data-testid={`lead-row-${it.id}`}
                  className={`border-b border-white/10 hover:bg-white/[0.03] cursor-pointer align-top ${isUnviewed ? "bg-red-500/[0.04]" : ""}`}
                  onClick={() => openLeadDetails(it)}
                >
                  <td className="px-5 pt-1 pb-3 text-white">
                    <div className="flex items-center gap-2">
                      {isUnviewed && (
                        <span
                          data-testid={`lead-new-dot-${it.id}`}
                          className="inline-block w-2 h-2 rounded-full bg-red-500 shadow-[0_0_0_3px_rgba(239,68,68,0.2)] animate-pulse"
                          title="Unviewed lead"
                        />
                      )}
                      <span>{displayName}</span>
                      {isUnviewed && (
                        <span
                          data-testid={`lead-new-badge-${it.id}`}
                          className="pill bg-red-500/15 text-red-300 border border-red-500/30 text-[9px] px-1.5 py-0.5"
                        >NEW</span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 pt-1 pb-3 text-slate-300">
                    <div className="flex items-center gap-2">
                      {it.photo_path && (
                        <CustomerVehiclePhoto path={it.photo_path} size="sm" />
                      )}
                      <span className="flex-1">{it.vehicle || "—"}</span>
                    </div>
                  </td>
                  <td className="px-5 pt-1 pb-3 text-slate-300">{it.service || "—"}</td>
                  <td className="px-5 pt-1 pb-3 text-slate-300 max-w-[240px]" data-testid={`lead-service-types-${it.id}`}>
                    {(() => {
                      const lbl = serviceTypesLabel(it);
                      return lbl && lbl !== (it.service || "")
                        ? <span className="text-xs leading-snug block">{lbl}</span>
                        : <span className="text-slate-500 italic text-xs">—</span>;
                    })()}
                  </td>
                  <td className="px-5 pt-1 pb-3 text-slate-400">
                    <div className="text-xs">{it.contact_email || it.email}</div>
                    <div className="text-xs">{it.contact_phone || it.phone}</div>
                  </td>
                  <td className="px-5 pt-1 pb-3 text-slate-400 whitespace-nowrap" title={absoluteTime(it.created_at)}>
                    <span data-testid={`lead-received-${it.id}`} className="text-xs">
                      {relativeTime(it.created_at) || "—"}
                    </span>
                  </td>
                  <td className="px-5 pt-1 pb-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex gap-2">
                      {it.sub_status !== "lost" && (
                        <button
                          data-testid={`lead-approve-${it.id}`}
                          onClick={() => convertToQuote(it)}
                          title="Approve — move to Quotes"
                          className="p-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 flex items-center gap-1 text-xs px-2"
                        >
                          <Check className="w-3.5 h-3.5" /> Approve
                        </button>
                      )}
                      {it.sub_status !== "lost" && (
                        <button
                          data-testid={`lead-reject-${it.id}`}
                          onClick={() => rejectLead(it)}
                          title="Reject — mark as lost"
                          className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-300 flex items-center gap-1 text-xs px-2"
                        >
                          <XCircle className="w-3.5 h-3.5" /> Reject
                        </button>
                      )}
                      <button data-testid={`lead-edit-${it.id}`} onClick={() => openEdit(it)} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300"><Pencil className="w-3.5 h-3.5" /></button>
                      <button data-testid={`lead-delete-${it.id}`} onClick={() => remove(it.id)} className="p-1.5 rounded-lg bg-white/5 hover:bg-red-500/20 text-slate-300 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </GlassPanel>

      {open && (
        <FormModal title={editing ? "Edit lead" : "New lead"} onClose={() => setOpen(false)} onSubmit={save}>
          <ContactPickerField
            value={form.contact_id}
            required
            testId="lead-customer"
            label="Customer"
            onChange={(c) => {
              if (!c) {
                setForm({ ...form, contact_id: "", contact_name: "", contact_email: "", contact_phone: "", contact_address: "" });
                return;
              }
              setForm({
                ...form,
                contact_id: c.id,
                contact_name: c.name || "",
                contact_email: c.email || "",
                contact_phone: c.phone || "",
                contact_address: c.address || "",
                vehicle: form.vehicle || c.vehicle || "",
              });
            }}
          />

          <JobTypeMultiSelect
            items={form.job_items || []}
            onChange={(items) => setForm({ ...form, job_items: items })}
            testId="lead-job-types"
          />

          {(form.job_items || []).length > 0 && (
            <div className="space-y-3">
              {form.job_items.map((it, idx) => (
                <JobItemForm
                  key={it.id}
                  item={it}
                  index={idx}
                  onPatch={(patch) => setForm({
                    ...form,
                    job_items: form.job_items.map((x, i) => i === idx ? { ...x, ...patch } : x),
                  })}
                  onRemove={() => setForm({
                    ...form,
                    job_items: form.job_items.filter((_, i) => i !== idx),
                  })}
                />
              ))}
            </div>
          )}

          <div>
            <label className="text-label block mb-2 flex items-center gap-1"><MapPin className="w-3 h-3" /> Install location</label>
            <div className="grid grid-cols-2 gap-2">
              {LOCATION_TYPES.map((l) => (
                <button
                  key={l.value}
                  type="button"
                  data-testid={`lead-location-${l.value}`}
                  onClick={() => setForm({ ...form, location_type: l.value })}
                  className={`text-sm rounded-2xl border py-2 transition ${form.location_type === l.value ? "bg-white text-black border-white font-semibold" : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06]"}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>
          {form.location_type === "off_site" && (
            <Field label="Off-site address" value={form.location_address} onChange={(v) => setForm({ ...form, location_address: v })} testId="lead-address" />
          )}

          <SelectField
            label="Status"
            value={form.sub_status}
            onChange={(v) => setForm({ ...form, sub_status: v })}
            options={SUB_STATUSES.lead.map((s) => ({ value: s.value, label: s.label }))}
            testId="lead-status"
          />
          <TextArea label="Internal Shop Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testId="lead-notes" hint="Internal only — customers won't see these notes." />
        </FormModal>
      )}
      {detailsFor && (
        <DetailsModal kind="lead" record={detailsFor} onClose={() => setDetailsFor(null)} />
      )}
    </div>
  );
}

export function FormModal({ title, onClose, onSubmit, extraActions = null, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
      <form
        onSubmit={onSubmit}
        className="glass-strong rounded-3xl p-7 w-full max-w-lg max-h-[90vh] overflow-y-auto space-y-4"
        data-testid="form-modal"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-2xl text-white">{title}</h2>
          <button type="button" data-testid="form-close" onClick={onClose} className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 text-slate-300">
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
        <div className="flex justify-end gap-2 pt-2 flex-wrap">
          <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full bg-white/5 border border-white/10 text-sm text-slate-300 hover:bg-white/10">Cancel</button>
          {extraActions}
          <button data-testid="form-save" type="submit" className="px-5 py-2.5 rounded-full bg-white text-black text-sm font-semibold hover:bg-slate-100">Save</button>
        </div>
      </form>
    </div>
  );
}

export function Field({ label, value, onChange, type = "text", required = false, testId, placeholder = "", hint = "" }) {
  return (
    <div>
      <label className="text-label block mb-1.5">{label}</label>
      <input
        data-testid={testId}
        type={type}
        value={value || ""}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
      />
      {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

export function TextArea({ label, value, onChange, testId, hint }) {
  return (
    <div>
      <label className="text-label block mb-1.5">{label}</label>
      <textarea
        data-testid={testId}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
      />
      {hint && <p className="text-[11px] text-slate-500 mt-1.5">{hint}</p>}
    </div>
  );
}

export function SelectField({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-label block mb-1.5">{label}</label>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white/30"
      >
        {options.map((o) => (
          <option key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value} className="bg-neutral-900">
            {typeof o === "string" ? o : o.label}
          </option>
        ))}
      </select>
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* AbandonedDraftsPanel                                                       */
/* -------------------------------------------------------------------------- */
/**
 * "Cart abandonment" for the public lead form — surfaces every session
 * where a customer typed name/phone/email but never pressed Submit. The
 * shop follow-up team can promote a draft into a real lead (kicking off
 * the normal pipeline + contact creation) or dismiss obvious spam.
 * Auto-refreshes every 60s; hides itself when nothing is pending so it
 * doesn't clutter the Leads page for shops with zero abandoned traffic.
 */
function AbandonedDraftsPanel({ onPromoted }) {
  const [drafts, setDrafts] = useState([]);
  const [busyId, setBusyId] = useState("");
  const [expanded, setExpanded] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/leads/abandoned");
      setDrafts(data.drafts || []);
    } catch (err) {
      // Silent — this is a nice-to-have panel, not a critical read
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const promote = async (draft) => {
    setBusyId(draft.id);
    try {
      await api.post(`/leads/abandoned/${draft.id}/promote`);
      toast.success(`Draft promoted — ${draft.name || draft.phone || draft.email} is now in Leads`);
      setDrafts((prev) => prev.filter((d) => d.id !== draft.id));
      onPromoted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to promote");
    } finally {
      setBusyId("");
    }
  };

  const dismiss = async (draft) => {
    setBusyId(draft.id);
    try {
      await api.post(`/leads/abandoned/${draft.id}/dismiss`);
      setDrafts((prev) => prev.filter((d) => d.id !== draft.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to dismiss");
    } finally {
      setBusyId("");
    }
  };

  if (drafts.length === 0) return null;

  return (
    <GlassPanel
      data-testid="abandoned-drafts-panel"
      className="!p-0 overflow-hidden border-amber-400/25 bg-amber-500/[0.04]"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-white/[0.03]"
        data-testid="abandoned-drafts-toggle"
      >
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-amber-500/20 border border-amber-400/40 flex items-center justify-center">
            <MailOpen className="w-3.5 h-3.5 text-amber-200" />
          </div>
          <div>
            <div className="text-sm text-white font-medium">
              Abandoned form drafts
              <span className="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-amber-400 text-black text-[10px] font-bold">
                {drafts.length}
              </span>
            </div>
            <div className="text-[11px] text-amber-100/70">
              Visitors who typed details on the public form but never submitted — reach out before they go cold
            </div>
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-amber-200/70">
          {expanded ? "Hide" : "Show"}
        </div>
      </button>
      {expanded && (
        <div className="border-t border-white/5 divide-y divide-white/5">
          {drafts.map((d) => {
            const vehicle = [d.year, d.make, d.model].filter(Boolean).join(" ") || d.item_description || "";
            const services = (d.services || []).join(", ");
            return (
              <div
                key={d.id}
                data-testid={`abandoned-draft-${d.id}`}
                className="px-5 py-3 flex items-start justify-between gap-4"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-white font-medium truncate">
                      {d.name || <span className="text-slate-500 italic">Unnamed visitor</span>}
                    </span>
                    {d.phone && (
                      <a
                        href={`tel:${d.phone}`}
                        className="inline-flex items-center gap-1 text-[11px] text-emerald-200 hover:text-emerald-100"
                        data-testid={`abandoned-draft-${d.id}-call`}
                      >
                        <PhoneCall className="w-3 h-3" /> {d.phone}
                      </a>
                    )}
                    {d.email && (
                      <a
                        href={`mailto:${d.email}?subject=Following up on your wrap enquiry`}
                        className="inline-flex items-center gap-1 text-[11px] text-sky-200 hover:text-sky-100"
                        data-testid={`abandoned-draft-${d.id}-email`}
                      >
                        <MailOpen className="w-3 h-3" /> {d.email}
                      </a>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-400 flex items-center gap-3 flex-wrap">
                    {vehicle && <span>{vehicle}</span>}
                    {services && <span className="text-amber-200/80">{services}</span>}
                    {d.registration && <span className="uppercase tracking-widest text-[10px] text-slate-500">Rego {d.registration}</span>}
                    <span className="text-slate-500">· {relativeTime(d.updated_at)}</span>
                  </div>
                  {d.notes && (
                    <div className="text-[11px] text-slate-500 italic truncate">"{d.notes}"</div>
                  )}
                </div>
                <div className="shrink-0 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => promote(d)}
                    disabled={busyId === d.id}
                    data-testid={`abandoned-draft-${d.id}-promote`}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-amber-400 text-black px-3 py-1.5 rounded-full hover:bg-amber-300 disabled:opacity-50"
                  >
                    <ArrowRightCircle className="w-3.5 h-3.5" /> Promote to lead
                  </button>
                  <button
                    type="button"
                    onClick={() => dismiss(d)}
                    disabled={busyId === d.id}
                    data-testid={`abandoned-draft-${d.id}-dismiss`}
                    className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 text-slate-400 hover:text-slate-200 disabled:opacity-50"
                    title="Dismiss"
                  >
                    <EyeOff className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </GlassPanel>
  );
}
